import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { XStack, YStack } from "tamagui";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import AuthScaffold from "@/components/AuthScaffold";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { consumeWebPostAuthRedirect } from "@/lib/postAuthRedirect";
import { AppButton, AppFieldGroup, AppInput } from "../../ui-foundation";

const VerifyMfa = () => {
  const navigate = useNavigate();
  const { user, signOut, refreshSecurity } = useAuth();
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    async function loadFactor() {
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.auth.mfa.listFactors();

      if (error) {
        console.error(error);
        toast.error("Could not load your authenticator setup.");
      } else {
        const verifiedFactor = data.totp.find((factor) => factor.status === "verified");
        setFactorId(verifiedFactor?.id || null);
      }

      setLoading(false);
    }

    void loadFactor();
  }, [user]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!factorId) {
      toast.error("No authenticator app is connected to this account.");
      return;
    }

    if (!code.trim()) {
      toast.error("Enter the 6-digit authenticator code.");
      return;
    }

    setVerifying(true);
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId,
      code: code.trim(),
    });

    if (error) {
      console.error(error);
      toast.error(error.message || "That code did not work.");
    } else {
      await refreshSecurity();
      toast.success("Two-step verification complete.");
      navigate(consumeWebPostAuthRedirect(), { replace: true });
    }

    setVerifying(false);
  };

  return (
    <AuthScaffold
      eyebrow="Two-step verification"
      title="One more code, then you're in."
      description="Enter the 6-digit code from your authenticator app to continue into Kindred."
    >
      <YStack tag="form" onSubmit={handleVerify} gap="$sm">
        <AppFieldGroup label={<label htmlFor="mfa-code">Authenticator code</label>}>
          <AppInput
            id="mfa-code"
            value={code}
            onChangeText={(value) => setCode(value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="123456"
            autoComplete="one-time-code"
            disabled={loading}
          />
        </AppFieldGroup>

        <AppButton type="submit" disabled={loading || verifying} width="100%">
          <XStack alignItems="center" gap="$xs">
            {verifying ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
          </XStack>
          {loading ? "Loading..." : verifying ? "Checking..." : "Verify and continue"}
        </AppButton>

        <AppButton type="button" tone="outline" onPress={() => void signOut()} width="100%">
          Sign out instead
        </AppButton>
      </YStack>
    </AuthScaffold>
  );
};

export default VerifyMfa;
